import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'frameworkSection',
    'policySection',
    'frameworkRadio',
    'policyRadio',
    'otherRadio',
    'frameworkSelect',
    'policySelect',
    'logoInput',
    'hiddenLogoInput',
    'iconSelect',
    'colorSelect'
  ]

  static values = {
    logoPresent: Boolean
  }

  declare frameworkSectionTarget: HTMLElement
  declare policySectionTarget: HTMLElement
  declare frameworkRadioTarget: HTMLInputElement
  declare policyRadioTarget: HTMLInputElement
  declare otherRadioTarget: HTMLInputElement
  declare frameworkSelectTarget: HTMLSelectElement
  declare policySelectTarget: HTMLSelectElement

  declare readonly hasLogoInputTarget: boolean
  declare readonly logoInputTarget: HTMLInputElement
  declare readonly hasHiddenLogoInputTarget: boolean
  declare readonly hiddenLogoInputTarget: HTMLInputElement
  declare readonly hasIconSelectTarget: boolean
  declare readonly iconSelectTarget: HTMLSelectElement
  declare readonly hasColorSelectTarget: boolean
  declare readonly colorSelectTarget: HTMLSelectElement
  declare logoPresentValue: boolean

  connect (): void {
    this.initializeSelection()
    this.updateVisibility()
    this.updateBrandingFields()
  }

  toggle (): void {
    if (this.isToggleDisabled()) {
      return
    }

    this.clearOtherField()
    this.updateVisibility()
  }

  private isToggleDisabled (): boolean {
    return this.frameworkRadioTarget.disabled || this.policyRadioTarget.disabled
  }

  private initializeSelection (): void {
    const hasFrameworkSelected = Array.from(this.frameworkSelectTarget.selectedOptions).length > 0
    const hasPolicySelected = Array.from(this.policySelectTarget.selectedOptions).length > 0

    if (hasPolicySelected) {
      this.policyRadioTarget.checked = true
    } else if (hasFrameworkSelected || this.isNewRecord()) {
      this.frameworkRadioTarget.checked = true
    } else {
      this.otherRadioTarget.checked = true
    }
  }

  private isNewRecord (): boolean {
    const newRecordField = document.getElementById('business_process_new_record') as HTMLInputElement
    return newRecordField?.value === 'true'
  }

  private updateVisibility (): void {
    if (this.frameworkRadioTarget.checked) {
      this.showFramework()
    } else if (this.policyRadioTarget.checked) {
      this.showPolicy()
    } else {
      this.hideAll()
    }
  }

  private showFramework (): void {
    this.frameworkSectionTarget.classList.remove('hidden')
    this.policySectionTarget.classList.add('hidden')
  }

  private showPolicy (): void {
    this.frameworkSectionTarget.classList.add('hidden')
    this.policySectionTarget.classList.remove('hidden')
  }

  private hideAll (): void {
    this.frameworkSectionTarget.classList.add('hidden')
    this.policySectionTarget.classList.add('hidden')
  }

  private clearOtherField (): void {
    if (this.isToggleDisabled()) return

    if (this.frameworkRadioTarget.checked) {
      this.clearSelect(this.policySelectTarget)
    } else if (this.policyRadioTarget.checked) {
      this.clearSelect(this.frameworkSelectTarget)
    } else {
      this.clearSelect(this.frameworkSelectTarget)
      this.clearSelect(this.policySelectTarget)
    }
  }

  private clearSelect (select: HTMLSelectElement): void {
    Array.from(select.options).forEach(option => {
      option.selected = false
    })
    select.dispatchEvent(new Event('change'))
  }

  logoSelected (): void {
    if (!this.hasIconSelectTarget || !this.hasColorSelectTarget) return

    this.updateLogoPresence(this.logoHasValue())
  }

  logoRemoved (): void {
    if (!this.hasIconSelectTarget || !this.hasColorSelectTarget) return

    this.clearLogoInputs()
    this.updateLogoPresence(false)
  }

  private updateBrandingFields (): void {
    if (!this.hasIconSelectTarget || !this.hasColorSelectTarget) return

    const hasLogo = this.logoPresentValue || this.logoHasValue()
    this.updateLogoPresence(hasLogo)
  }

  private updateLogoPresence (present: boolean): void {
    this.logoPresentValue = present
    this.toggleIconAndColorFields()
  }

  private toggleIconAndColorFields (): void {
    const disabled = this.logoPresentValue

    if (this.hasIconSelectTarget) this.toggleSelect(this.iconSelectTarget, disabled)
    if (this.hasColorSelectTarget) this.toggleSelect(this.colorSelectTarget, disabled)
  }

  private toggleSelect (select: HTMLSelectElement, disabled: boolean): void {
    select.disabled = disabled

    const plugin = (select as unknown as { tomselect?: { disable: () => void, enable: () => void } }).tomselect
    if (plugin != null) {
      disabled ? plugin.disable() : plugin.enable()
    }
  }

  private logoHasValue (): boolean {
    const hasFile = this.hasLogoInputTarget && (this.logoInputTarget.files?.length ?? 0) > 0
    const hasHiddenValue = this.hasHiddenLogoInputTarget && this.hiddenLogoInputTarget.value.trim().length > 0

    return hasFile || hasHiddenValue
  }

  private clearLogoInputs (): void {
    if (this.hasLogoInputTarget) {
      this.logoInputTarget.value = ''
    }

    if (this.hasHiddenLogoInputTarget) {
      this.hiddenLogoInputTarget.value = ''
    }
  }
}
